import React from 'react';
import { CustomReportResponse } from '@/services/fsiReport';
import { 
  CheckCircle, Building, DollarSign, TrendingUp, AlertCircle, 
  Info, Layers, Home, MapPin, Calculator, BarChart3, FileText
} from 'lucide-react';

interface CustomReportDisplayProps {
  report: CustomReportResponse;
}

const CustomReportDisplay: React.FC<CustomReportDisplayProps> = ({ report }) => {
  const formatCurrency = (amount: number | undefined | null) => {
    if (amount === undefined || amount === null) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number | undefined | null, decimals: number = 2) => {
    if (num === undefined || num === null) return 'N/A';
    return num.toLocaleString('en-IN', { maximumFractionDigits: decimals });
  };

  return (
    <div className="space-y-6">
      {/* Success Header */}
      <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white shadow-lg">
        <div className="flex items-center gap-3 mb-3">
          <CheckCircle className="w-8 h-8" />
          <div>
            <h3 className="text-2xl font-bold">Report Generated Successfully!</h3>
            <p className="text-green-100">
              Custom FSI analysis for {report.selected_options?.development_scenario?.name || 'Selected Development'}
            </p>
          </div>
        </div>
      </div>

      {/* Key Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="w-5 h-5 text-blue-500" />
            <span className="text-sm text-gray-600">Plot Area</span>
          </div>
          <p className="text-2xl font-bold text-gray-800">
            {formatNumber(report.summary?.plot_area_sqm)} m²
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
          <div className="flex items-center gap-2 mb-2">
            <Calculator className="w-5 h-5 text-green-500" />
            <span className="text-sm text-gray-600">FSI Utilized</span>
          </div>
          <p className="text-2xl font-bold text-gray-800">
            {formatNumber(report.summary?.total_fsi_utilized)}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500">
          <div className="flex items-center gap-2 mb-2">
            <Building className="w-5 h-5 text-purple-500" />
            <span className="text-sm text-gray-600">Buildable Area</span>
          </div>
          <p className="text-2xl font-bold text-gray-800">
            {formatNumber(report.summary?.total_buildable_area_sqm)} m²
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-500">
          <div className="flex items-center gap-2 mb-2">
            <Layers className="w-5 h-5 text-orange-500" />
            <span className="text-sm text-gray-600">Total Floors</span>
          </div>
          <p className="text-2xl font-bold text-gray-800">
            {report.summary?.number_of_floors || 'N/A'}
          </p>
        </div>
      </div>

      {/* FSI Breakdown */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-green-600" />
          FSI Breakdown
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Base FSI</p>
            <p className="text-xl font-bold text-blue-600">
              {formatNumber(report.fsi_breakdown?.fsi_components?.base)}
            </p>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Premium FSI</p>
            <p className="text-xl font-bold text-green-600">
              {formatNumber(report.fsi_breakdown?.fsi_components?.premium)}
            </p>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Paid FSI</p>
            <p className="text-xl font-bold text-purple-600">
              {formatNumber(report.fsi_breakdown?.fsi_components?.paid)}
            </p>
          </div>
          <div className="text-center p-3 bg-orange-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">TDR FSI</p>
            <p className="text-xl font-bold text-orange-600">
              {formatNumber(report.fsi_breakdown?.fsi_components?.tdr)}
            </p>
          </div>
        </div>
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="font-medium text-gray-700">Total Selected FSI:</span>
            <span className="text-xl font-bold text-gray-800">
              {formatNumber(report.fsi_breakdown?.selected_fsi)}
            </span>
          </div>
        </div>
      </div>

      {/* Cost Analysis */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-green-600" />
          Cost Analysis
        </h4>
        <div className="space-y-3">
          <div className="flex justify-between p-3 bg-gray-50 rounded">
            <span className="text-gray-600">Base Cost per sqft:</span>
            <span className="font-bold text-gray-800">
              ₹{formatNumber(report.cost_analysis?.base_cost_per_sqft)}
            </span>
          </div>
          <div className="flex justify-between p-3 bg-gray-50 rounded">
            <span className="text-gray-600">FSI Utilization Multiplier:</span>
            <span className="font-bold text-gray-800">
              {report.cost_analysis?.fsi_utilization_multiplier || 'N/A'}x
            </span>
          </div>
          <div className="flex justify-between p-3 bg-gray-50 rounded">
            <span className="text-gray-600">Effective Cost per sqft:</span>
            <span className="font-bold text-gray-800">
              ₹{formatNumber(report.cost_analysis?.effective_cost_per_sqft)}
            </span>
          </div>
          <div className="flex justify-between p-4 bg-green-50 rounded-lg border-2 border-green-200">
            <span className="text-lg font-medium text-gray-700">Total Construction Cost:</span>
            <span className="text-xl font-bold text-green-600">
              {formatCurrency(report.cost_analysis?.total_construction_cost_inr)}
            </span>
          </div>
          <div className="flex justify-between p-3 bg-gray-50 rounded">
            <span className="text-gray-600">In Crores:</span>
            <span className="font-bold text-gray-800">
              ₹{formatNumber(report.cost_analysis?.total_construction_cost_crores)} Cr
            </span>
          </div>
        </div>
      </div>

      {/* Financial Projections */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-green-600" />
          Financial Projections
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg text-center">
            <p className="text-sm text-gray-600 mb-2">Estimated Sale Value</p>
            <p className="text-xl font-bold text-blue-600">
              {formatCurrency(report.summary?.estimated_sale_value_inr)}
            </p>
          </div>
          <div className="p-4 bg-green-50 rounded-lg text-center">
            <p className="text-sm text-gray-600 mb-2">Gross Profit</p>
            <p className="text-xl font-bold text-green-600">
              {formatCurrency(report.summary?.gross_profit_inr)}
            </p>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg text-center">
            <p className="text-sm text-gray-600 mb-2">Profit Margin</p>
            <p className="text-xl font-bold text-purple-600">
              {formatNumber(report.summary?.profit_margin_percentage)}%
            </p>
          </div>
        </div>
      </div>

      {/* Area Calculations */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Home className="w-5 h-5 text-green-600" />
          Detailed Area Calculations
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex justify-between p-2 border-b">
              <span className="text-gray-600">Plot Area (m²):</span>
              <span className="font-bold">{formatNumber(report.area_calculations?.plot_area_sqm)}</span>
            </div>
            <div className="flex justify-between p-2 border-b">
              <span className="text-gray-600">Plot Area (sqft):</span>
              <span className="font-bold">{formatNumber(report.area_calculations?.plot_area_sqft)}</span>
            </div>
            <div className="flex justify-between p-2 border-b">
              <span className="text-gray-600">Base Buildable Area (m²):</span>
              <span className="font-bold">{formatNumber(report.area_calculations?.base_buildable_area_sqm)}</span>
            </div>
            <div className="flex justify-between p-2 border-b">
              <span className="text-gray-600">TDR Additional Area (m²):</span>
              <span className="font-bold">{formatNumber(report.area_calculations?.tdr_additional_area_sqm)}</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between p-2 border-b">
              <span className="text-gray-600">Total Floors:</span>
              <span className="font-bold">{report.area_calculations?.floor_details?.total_floors || 'N/A'}</span>
            </div>
            <div className="flex justify-between p-2 border-b">
              <span className="text-gray-600">Basement Floors:</span>
              <span className="font-bold">{report.area_calculations?.floor_details?.basement_floors || 0}</span>
            </div>
            <div className="flex justify-between p-2 border-b">
              <span className="text-gray-600">Above Ground Floors:</span>
              <span className="font-bold">{report.area_calculations?.floor_details?.above_ground_floors || 'N/A'}</span>
            </div>
            <div className="flex justify-between p-2 border-b">
              <span className="text-gray-600">Floor Plate Area (m²):</span>
              <span className="font-bold">{formatNumber(report.area_calculations?.floor_details?.floor_plate_area_sqm)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Important Notes */}
      {report.important_notes && report.important_notes.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-yellow-600" />
            Important Notes & Requirements
          </h4>
          <div className="space-y-3">
            {report.important_notes.map((note, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg flex items-start gap-3 ${
                  note.type === 'payment' ? 'bg-red-50 border border-red-200' :
                  note.type === 'requirement' ? 'bg-blue-50 border border-blue-200' :
                  'bg-gray-50 border border-gray-200'
                }`}
              >
                <Info className={`w-5 h-5 mt-0.5 ${
                  note.type === 'payment' ? 'text-red-600' :
                  note.type === 'requirement' ? 'text-blue-600' :
                  'text-gray-600'
                }`} />
                <p className="text-sm text-gray-700">{note.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Specialized Analysis Sections */}
      {report.specialized_analysis && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-green-600" />
            Specialized Analysis
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {report.specialized_analysis?.basement_development && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <h5 className="font-bold text-gray-800 mb-2">Basement Development</h5>
                <p className="text-sm text-gray-600">
                  {report.specialized_analysis.basement_development?.basement_floors || 0} basement level(s) included
                </p>
              </div>
            )}
            {report.specialized_analysis.paid_fsi_details && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <h5 className="font-bold text-gray-800 mb-2">Paid FSI Details</h5>
                <p className="text-sm text-gray-600">
                  Additional FSI purchased for enhanced development
                </p>
              </div>
            )}
            {report.specialized_analysis.residential_development && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <h5 className="font-bold text-gray-800 mb-2">Residential Development</h5>
                <p className="text-sm text-gray-600">
                  Optimized for residential usage
                </p>
              </div>
            )}
            {report.specialized_analysis.commercial_development && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <h5 className="font-bold text-gray-800 mb-2">Commercial Development</h5>
                <p className="text-sm text-gray-600">
                  Configured for commercial purposes
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomReportDisplay;
